/**
 * The Unclaimed tab: bank credits nobody has claimed, oldest first, and the record of
 * who looked at each and what they decided.
 *
 * Recording a look moves no money. `waiting` buys a stated number of days and then the
 * credit ages again on its own; `return_decided` records the decision only, and the money
 * leaves through the two-person, step-up-gated return.
 */
import { useCallback, useEffect, useState } from 'react';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import opsService, { type UnclaimedBand, type UnclaimedCredit } from '../../services/ops.service';
import authService from '../../services/auth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import {
  AGENT_RETURN_NOTE,
  BAND_LABEL,
  EMPTY_REVIEW,
  OUTCOME_LABEL,
  QUIET_DAYS_MAX,
  QUIET_DAYS_MIN,
  RETURN_DECIDED_NOTE,
  isManagerRole,
  listView,
  oldestFirst,
  outcomesFor,
  reviewProblem,
  submitReview,
  type ListView,
  type ReviewDraft,
} from './bankRecon.helpers';

const BAND_TONE: Record<UnclaimedBand, BadgeTone> = {
  fresh: 'neutral',
  unexplained: 'warning',
  escalated: 'danger',
  overdue: 'danger',
  return_due: 'danger',
};

const inputStyle = { background: colors.canvas, color: colors.ink, border: `1px solid ${colors.border}` };

export default function OpsUnclaimedTab(): JSX.Element {
  const role = authService.getRole();
  const [rows, setRows] = useState<UnclaimedCredit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(oldestFirst(await opsService.unclaimed()));
    } catch (e: unknown) {
      setRows([]);
      setError(getApiErrorMessage(e, 'Could not load the unclaimed credits.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <UnclaimedView
      view={listView({ loading, error, count: rows.length })}
      error={error}
      flash={flash}
      rows={rows}
      role={role}
      onReviewed={(message) => { setFlash(message); void reload(); }}
    />
  );
}

export function UnclaimedView({ view, error, flash, rows, role, onReviewed }: Readonly<{
  view: ListView;
  error: string | null;
  flash: string | null;
  rows: UnclaimedCredit[];
  role: string | null;
  onReviewed: (message: string) => void;
}>): JSX.Element {
  return (
    <div>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Money that arrived at the bank and nobody has claimed, oldest first. Recording a look
        here moves no money: it says someone checked, and what they decided. Only matching,
        voiding or sending a credit back takes it off this list.
      </p>
      {flash && (
        <div className="rounded-lg p-3 mb-4 text-sm"
             style={{ background: colors.successBg, color: colors.success, border: `1px solid ${colors.successBorder}` }}>
          {flash}
        </div>
      )}
      {view === 'error' && error && (
        <div className="rounded-lg p-3 mb-4 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      {view === 'loading' && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}
      {view === 'empty' && (
        <div className="rounded-2xl p-6 text-sm"
             style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
          No credit is waiting to be claimed.
        </div>
      )}
      {view === 'rows' && (
        <ul className="space-y-3">
          {rows.map((c) => (
            <UnclaimedRow key={c.id} credit={c} role={role} onReviewed={onReviewed} />
          ))}
        </ul>
      )}
    </div>
  );
}

function UnclaimedRow({ credit, role, onReviewed }: Readonly<{
  credit: UnclaimedCredit;
  role: string | null;
  onReviewed: (message: string) => void;
}>): JSX.Element {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ReviewDraft>(EMPTY_REVIEW);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const result = await submitReview(opsService, credit.id, draft, role);
    setBusy(false);
    if (result.ok) {
      setOpen(false);
      setDraft(EMPTY_REVIEW);
      onReviewed(result.message);
    } else {
      setError(result.error);
    }
  };

  return (
    <li className="rounded-2xl p-4"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <UnclaimedSummary credit={credit} />
      <div className="mt-3">
        <button type="button" onClick={() => setOpen((v) => !v)}
                className="text-xs px-3 py-1.5 rounded-md font-semibold"
                style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
          {open ? 'Cancel' : 'Record a review'}
        </button>
      </div>
      {open && (
        <ReviewForm
          idPrefix={credit.id}
          draft={draft}
          role={role}
          busy={busy}
          error={error}
          onChange={setDraft}
          onSubmit={() => { void submit(); }}
        />
      )}
    </li>
  );
}

export function UnclaimedSummary({ credit }: Readonly<{ credit: UnclaimedCredit }>): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="font-semibold" style={{ color: colors.ink }}>
          {String(credit.amount)} {credit.currency_code}
        </div>
        <div className="text-xs" style={{ color: colors.muted }}>
          {credit.bank_name} · {credit.market} · arrived {formatDateTime(credit.posted_at)} · {credit.age_days} day(s) ago
        </div>
        {credit.reference_text && (
          <div className="text-xs mt-0.5" style={{ color: colors.inkSoft }}>
            &ldquo;{credit.reference_text}&rdquo;
          </div>
        )}
        {credit.band === 'return_due' && (
          <div className="text-xs mt-1 font-semibold" style={{ color: colors.danger }}>
            Nobody identified this transfer within 90 days. Send it back to the account it came
            from with the reason &ldquo;Nobody identified it within 90 days&rdquo;.
          </div>
        )}
        {(credit.deposit_notices ?? []).map((n) => (
          <div key={n.notice_id} className="text-xs mt-1" style={{ color: colors.inkSoft }}>
            <span className="font-semibold">Client notice:</span>{' '}
            <span>{n.client_name}</span>{' · '}
            <span>{n.product_name}</span>{' · '}
            <span>{n.currency_code} {n.amount}</span>{' · '}
            <span>{n.sent_on}</span>
            {n.same_account && <span> · Same account</span>}
            {n.same_amount && <span> · Same amount</span>}
          </div>
        ))}
        {credit.last_outcome && (
          <div className="text-xs mt-1" style={{ color: colors.inkSoft }}>
            Last look{credit.last_reviewed_at ? ` ${formatDateTime(credit.last_reviewed_at)}` : ''}:
            {' '}{credit.last_outcome.replace('_', ' ')}
            {credit.last_reason ? ` — ${credit.last_reason}` : ''}
            {credit.quiet_until ? ` (quiet until ${credit.quiet_until})` : ''}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {credit.needs_a_manager && <StatusBadge tone="danger">Needs a manager</StatusBadge>}
        <StatusBadge tone={BAND_TONE[credit.band]}>{BAND_LABEL[credit.band]}</StatusBadge>
      </div>
    </div>
  );
}

export function ReviewForm({ idPrefix, draft, role, busy, error, onChange, onSubmit }: Readonly<{
  idPrefix: string;
  draft: ReviewDraft;
  role: string | null;
  busy: boolean;
  error: string | null;
  onChange: (draft: ReviewDraft) => void;
  onSubmit: () => void;
}>): JSX.Element {
  const problem = reviewProblem(draft, role);
  const disabled = problem !== null || busy;
  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
      {error && (
        <div className="rounded-lg p-2 mb-3 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      <label htmlFor={`${idPrefix}-outcome`} className="block text-xs font-semibold mb-1"
             style={{ color: colors.inkSoft }}>
        What did you find?
      </label>
      <select id={`${idPrefix}-outcome`} value={draft.outcome} disabled={busy}
              onChange={(e) => onChange({ ...draft, outcome: e.target.value as ReviewDraft['outcome'] })}
              className="w-full px-3 py-2 rounded-lg text-sm mb-3" style={inputStyle}>
        <option value="">— choose —</option>
        {outcomesFor(role).map((o) => <option key={o} value={o}>{OUTCOME_LABEL[o]}</option>)}
      </select>
      {!isManagerRole(role) && (
        <p className="text-xs mb-3" style={{ color: colors.mutedSoft }}>{AGENT_RETURN_NOTE}</p>
      )}
      {draft.outcome === 'return_decided' && (
        <p className="text-xs mb-3" style={{ color: colors.mutedSoft }}>{RETURN_DECIDED_NOTE}</p>
      )}
      {draft.outcome === 'waiting' && (
        <>
          <label htmlFor={`${idPrefix}-days`} className="block text-xs font-semibold mb-1"
                 style={{ color: colors.inkSoft }}>
            Days it waits ({QUIET_DAYS_MIN}–{QUIET_DAYS_MAX})
          </label>
          <input id={`${idPrefix}-days`} value={draft.quietDays} disabled={busy} inputMode="numeric"
                 onChange={(e) => onChange({ ...draft, quietDays: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                 className="w-24 px-3 py-2 rounded-lg text-sm mb-3" style={inputStyle} />
        </>
      )}
      <label htmlFor={`${idPrefix}-reason`} className="block text-xs font-semibold mb-1"
             style={{ color: colors.inkSoft }}>
        What did you find, and what is it waiting on?
      </label>
      <textarea id={`${idPrefix}-reason`} rows={2} value={draft.reason} disabled={busy}
                onChange={(e) => onChange({ ...draft, reason: e.target.value })}
                className="w-full px-3 py-2 rounded-lg text-sm mb-2" style={inputStyle} />
      {disabled && problem && (
        <p className="text-xs mb-2" style={{ color: colors.warning }}>{problem}</p>
      )}
      <button type="button" disabled={disabled} onClick={onSubmit}
              className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              style={{ background: colors.navy, color: '#FFFFFF' }}>
        {busy ? 'Recording…' : 'Record review'}
      </button>
    </div>
  );
}
